import {Drawer} from 'components/customer/Product/entity/Drawer';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {
    dragLimits,
    InnerDrawerBox,
} from 'components/customer/Product/Drawer/helpers/innerDrawerGeometry';

interface LimitsInput {
    // the drawers within the count, top first
    drawers: Drawer[];
    cabinetHeight: number;
    // what validation takes off the height above the top drawer: the rail's
    // width when it is on edge, else the carcase thickness
    topPart: number;
    // a negative drawer top or bottom comes off the face the runner lookup is
    // given, for the top or bottom drawer
    drawerTop: number;
    drawerBottom: number;
    geometry: {floor: number; ceiling: number; boxes: InnerDrawerBox[]} | null;
}

const height = (drawer: Drawer) => toNumber(drawer.drawer_runner_height);
const top = (drawer: Drawer) => toNumber(drawer.systemTopClearance);
const innerBottom = (drawer: Drawer) =>
    toNumber(drawer.systemInnerDrawerBottomClearance);

// to the hundredth, inwards, so a limit never lets through what it guards
const inwards = ({min, max}: {min: number; max: number}) => ({
    min: Math.ceil(min * 100 - 1e-6) / 100,
    max: Math.floor(max * 100 + 1e-6) / 100,
});

// Where a drawer can be moved to without validation rejecting it or any
// runner having to change, and clear of the other boxes on the drawing.
// A move takes its height from this drawer's face and gives it to the next
// drawer's, or the reverse, and a runner is kept only while its height and
// the top and inner bottom clearances fit the face.
export const innerDrawerLimits = (
    index: number,
    {
        drawers,
        cabinetHeight,
        topPart,
        drawerTop,
        drawerBottom,
        geometry,
    }: LimitsInput
) => {
    const drawer = drawers[Number(index)];

    if (!drawer) return {min: 0, max: Infinity};

    const lowest = innerBottom(drawer);
    let {min, max} =
        geometry === null
            ? {min: lowest, max: Infinity}
            : dragLimits(geometry.boxes, index, {
                  floor: geometry.floor,
                  ceiling: geometry.ceiling,
                  lowest,
              });

    const position = toNumber(drawer.position_to_bottom);
    const above = drawers[index - 1];
    const below = drawers[index + 1];
    const last = drawers.length - 1;

    const face = (of: Drawer, at: number) =>
        toNumber(of.drawer_face_height) +
        (at === 0 && drawerTop < 0 ? drawerTop : 0) +
        (at === last && drawerBottom < 0 ? drawerBottom : 0);
    const spare = (of: Drawer, at: number) =>
        face(of, at) - (height(of) + top(of) + innerBottom(of));

    // its top clearance under whatever closes the top, as the drawing shows it;
    // validation's own top takes off one thickness, so on a closed top it
    // would leave less than the clearance
    if (index === 0 && geometry !== null) {
        max = Math.min(
            max,
            geometry.ceiling - geometry.floor - (top(drawer) + height(drawer))
        );
    }

    // the product's drawer_position_to_bottom ranges, as validation runs them
    if (index === 0) {
        max = Math.min(
            max,
            cabinetHeight - (topPart + top(drawer) + height(drawer))
        );
    }

    if (above) {
        max = Math.min(
            max,
            toNumber(above.position_to_bottom) -
                (innerBottom(above) + top(drawer) + height(drawer))
        );
    }

    if (below) {
        min = Math.max(
            min,
            toNumber(below.position_to_bottom) +
                innerBottom(drawer) +
                top(below) +
                height(below)
        );
    }

    // up shrinks this drawer's face, down the next one's
    max = Math.min(max, position + spare(drawer, index));

    if (below) min = Math.max(min, position - spare(below, index + 1));

    return inwards({min, max});
};
